import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IBusinessSettings } from '../models/business-settings.model';
import { BusinessOperationsSettings } from '../models/business-operations-settings.model';
import { IXeroTenant } from '../../xero/models/xero-tenant.model';
import { IBusiness } from '../models/business.model';
import { PayrollSettings } from '../models/payroll-settings.model';

export const LS_XERO_BUSINESS_ID = 'xero-business-id';
export const LS_XERO_STATE = 'xero-state';
export const LS_LAST_BUSINESS_ID = 'avmin.business:last.id';

const API_BUSINESSES_URL = `${environment.apiUrl}/businesses`;

@Injectable({ providedIn: 'root' })
export class BusinessService {
  private readonly _http = inject(HttpClient);

  disconnectXero(businessId: number): Observable<void> {
    return this._http.delete<undefined>(`${API_BUSINESSES_URL}/${businessId}/xero`);
  }

  exchangeXeroCode(businessId: number, code: string): Observable<IXeroTenant[]> {
    return this._http.post<IXeroTenant[]>(`${API_BUSINESSES_URL}/${businessId}/xero/token`, { code });
  }

  find(businessId: number): Observable<IBusiness> {
    return this._http.get<IBusiness>(`${API_BUSINESSES_URL}/${businessId}`);
  }

  forPilot(pilotId: number): Observable<IBusiness[]> {
    return this._http.get<IBusiness[]>(`${API_BUSINESSES_URL}/pilot/${pilotId}`);
  }

  get(): Observable<IBusiness[]> {
    return this._http.get<IBusiness[]>(API_BUSINESSES_URL);
  }

  getLastBusinessId(): number | undefined {
    const businessId = localStorage.getItem(LS_LAST_BUSINESS_ID);
    return businessId === null ? undefined : Number(businessId);
  }

  getOperationsSettings(businessId: number): Observable<BusinessOperationsSettings> {
    return this._http.get<BusinessOperationsSettings>(`${API_BUSINESSES_URL}/${businessId}/settings/operations`);
  }

  getPayrollSettings(businessId: number): Observable<PayrollSettings> {
    return this._http.get<PayrollSettings>(`${API_BUSINESSES_URL}/${businessId}/settings/payroll`);
  }

  getSettings(businessId: number): Observable<IBusinessSettings> {
    return this._http.get<IBusinessSettings>(`${API_BUSINESSES_URL}/${businessId}/settings`);
  }

  setLastBusinessId(businessId: number): void {
    localStorage.setItem(LS_LAST_BUSINESS_ID, businessId.toString());
  }

  setXeroTenantId(businessId: number, tenantId: string) {
    return this._http.post<undefined>(`${API_BUSINESSES_URL}/${businessId}/xero/tenant/${tenantId}`, {});
  }

  syncAccounts(): Observable<undefined> {
    return this._http.post<undefined>(`${API_BUSINESSES_URL}/sync-accounts`, null);
  }

  updateOperationsSettings(
    businessId: number,
    settings: BusinessOperationsSettings
  ): Observable<BusinessOperationsSettings> {
    return this._http.put<BusinessOperationsSettings>(
      `${API_BUSINESSES_URL}/${businessId}/settings/operations`,
      settings
    );
  }

  updatePayrollSettings(businessId: number, settings: PayrollSettings): Observable<PayrollSettings> {
    return this._http.put<PayrollSettings>(`${API_BUSINESSES_URL}/${businessId}/settings/payroll`, settings);
  }
}
